import { css } from 'lit';

// Design tokens mirror Photon's theme::v2 (cpopsa/src/stem_gui/Theme.hpp);
// the comments give the Figma variable names. Geometry follows theme::v2::geo
// and StemRowLayout.hpp. Fonts come from styles/fonts.ts (@font-face is
// injected into the host document; the family names below match it).
export const widgetStyles = css`
  :host {
    /* Grey/blue ramp                       Figma var */
    --p-bg: #06080a;             /* --4   window background */
    --p-panel: #0c1114;          /* --8   island gradient top */
    --p-well: #12191f;           /* --12 */
    --p-hover: #182129;          /* --16  hover; input fill */
    --p-pressed: #25323d;        /* --24  pressed; island border */
    --p-track: #314352;          /* --32  slider track */
    --p-active-pressed: #3d5366; /* --40  input border; act+press */
    --p-active-hover: #49647a;   /* --48 */
    --p-active: #53708a;         /* --56  toggle-on */
    --p-muted-icon: #7e878f;     /* --56 grey ramp: outlines, version tags */
    --p-text: #a9b1b8;           /* --72  labels / idle glyphs */
    --p-text-bright: #f5f5f5;    /* --96  active text / glyphs */
    --p-focus: #70a9ff;          /* voice ring tone — keyboard focus ring */
    --p-playhead: #a9b1b8;       /* --72: the app's marker + line (playheadFragmentShader) */

    --p-font: 'Photon Roboto', Roboto, -apple-system, BlinkMacSystemFont, 'Segoe UI',
      Helvetica, Arial, sans-serif;
    --p-mono: 'Photon Roboto Mono', 'Roboto Mono', ui-monospace, Menlo, Consolas, monospace;

    --p-radius-island: 8px;
    --p-radius-btn: 4px;
    --p-pane-w: 280px;           /* geo::rightPaneW */
    --p-ruler-h: 30px;           /* kTimeRulerHeight; must match ui/ruler.ts RULER_HEIGHT */

    display: block;
    width: 100%;
    max-width: 1104px;
    container: photon / inline-size;
    font-family: var(--p-font);
    font-size: 12px;
    line-height: 1;
    color: var(--p-text);
    -webkit-font-smoothing: antialiased;
    /* Prevent text selection from interfering with scrub/drag. */
    user-select: none;
    -webkit-user-select: none;
  }

  *, *::before, *::after {
    box-sizing: border-box;
  }

  button {
    font: inherit;
    color: inherit;
    cursor: pointer;
  }
  button:disabled {
    cursor: default;
  }
  svg {
    display: block;
  }

  /* ---- Card = the app window ------------------------------------------- */

  /* No overflow clipping here: the model menu popup hangs below the card. */
  .card {
    background: var(--p-bg);
    border-radius: var(--p-radius-island);
    padding: 0 12px 12px;
  }

  /* ---- Top nav (header zone 64 = 12 + 40 + 12) --------------------------- */

  /* One 40px row at the design width; islands wrap onto further rows as
     soon as they run out of room (the single row needs ~980px), rather than
     overflowing until the narrow layout kicks in. The branding icon is
     pinned top-right and the padding keeps the rows clear of it. */
  .nav {
    position: relative;
    min-height: 64px;
    padding: 12px 40px 12px 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
  }

  .brand {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--p-text-bright);
    margin-right: 12px;
  }

  /* iZotope branding icon: 28px, --72, right-anchored at the nav's edge
     (TopNavBar brandingRect_: W − 12 margin − 28; the card's own 12px
     padding is that margin). */
  .branding {
    position: absolute;
    top: 18px;             /* 12 pad + (40 − 28) / 2 */
    right: 0;
    width: 28px;
    height: 28px;
    color: var(--p-text);
    display: inline-flex;
  }

  /* NavIsland: gradient panel -> transparent, 1px pressedBg, r8, pad 5, gap 4 */
  .island {
    display: flex;
    align-items: center;
    height: 40px;
    padding: 0 5px;
    gap: 4px;
    border-radius: var(--p-radius-island);
    border: 1px solid var(--p-pressed);
    background: linear-gradient(to bottom, var(--p-panel), rgba(12, 17, 20, 0));
  }
  /* Timecode reads in --72 like the transport glyphs (TopNavBar
     setupTimeDisplays), not the bright active tone. */
  .island.timestamp {
    padding: 0 12px;
    gap: 0;
    font-family: var(--p-mono);
    color: var(--p-text);
    white-space: nowrap;
  }
  /* Meter island: the app's design width (rebrand 6667:71172: island 282 =
     pad 12 + 258 content + pad 12). The 28px child holds the 8px bar + labels. */
  .island.meter {
    flex: 0 0 auto;
    width: 282px;
    max-width: 100%;
    padding: 0 11px;   /* + 1px border = the design's 12px inset, bar 258 */
  }
  canvas.meter {
    display: block;
    width: 100%;
    height: 28px;
  }

  /* ThemedButton: fill = buttonFill(active, interaction), r4, natural-size glyph */
  .island-btn {
    position: relative;
    width: 30px;
    height: 30px;
    padding: 0;
    border: 0;
    border-radius: var(--p-radius-btn);
    background: transparent;
    color: var(--p-text);
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .island-btn:hover:not(:disabled) {
    background: var(--p-hover);
  }
  .island-btn:active:not(:disabled) {
    background: var(--p-pressed);
  }
  .island-btn.active {
    background: var(--p-active);
    color: var(--p-text-bright);
  }
  .island-btn.active:hover:not(:disabled) {
    background: var(--p-active-hover);
  }
  .island-btn.active:active:not(:disabled) {
    background: var(--p-active-pressed);
  }
  .island-btn:disabled {
    opacity: 0.4;
  }
  /* Text island buttons (Bypass): Roboto 14 label, designer's 66px Bypass */
  .island-btn.text-btn {
    width: auto;
    min-width: 66px;
    padding: 0 12px;
    font-size: 14px;
  }
  .island-btn.outlined {
    border: 1px solid var(--p-active-pressed);
  }

  /* Pulse advertises interactivity to users scrolling past — runs once every
     2 s until the user starts playback (click or spacebar). The button itself
     scales; an absolutely-positioned ::before paints an expanding white halo
     so we don't fight the button's own hover styles. */
  .play-btn.pulse {
    animation: pPlayPulseScale 2000ms ease-in-out infinite;
  }
  .play-btn.pulse::before {
    content: '';
    position: absolute;
    inset: 0;
    /* Circular halo: the island button is a rounded square, and a square
       halo overlapping the seek buttons reads as a glitch. */
    border-radius: 50%;
    pointer-events: none;
    animation: pPlayPulseRing 2000ms ease-out infinite;
  }
  .play-btn.pulse:hover,
  .play-btn.pulse:focus-visible,
  .play-btn.pulse:hover::before,
  .play-btn.pulse:focus-visible::before {
    animation-play-state: paused;
  }
  @keyframes pPlayPulseScale {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.2); }
  }
  @keyframes pPlayPulseRing {
    0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.85); }
    100% { box-shadow: 0 0 0 20px rgba(255, 255, 255, 0); }
  }

  /* ---- Body: waveform column + right pane ------------------------------- */

  .body {
    display: grid;
    grid-template-columns: minmax(0, 1fr) var(--p-pane-w);
    gap: 12px;
    align-items: stretch;
  }

  .left {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .wave-stack {
    position: relative;
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  /* Normal arrow on the ruler itself, as in the app (only the grip is a
     hand); a click or drag still seeks. */
  canvas.ruler {
    display: block;
    width: 100%;
    height: var(--p-ruler-h);
    touch-action: none;
    outline: none;
  }

  /* The waveform box: 1px --56 border on all four edges (design 6190:4595,
     the renderer's borderFragmentShader). */
  .wave-frame {
    position: relative;
    flex: 1 1 auto;
    min-height: 240px;
    border: 1px solid var(--p-muted-icon);
    overflow: hidden;
  }

  /* Absolutely positioned so the canvas's own bitmap size never feeds back
     into the frame's height (a 100%-height canvas in an auto-height box
     reports its pixel height as intrinsic size and the layout ratchets). */
  canvas.waveform {
    position: absolute;
    inset: 0;
    display: block;
    width: 100%;
    height: 100%;
    cursor: pointer;
    /* Removes the legacy ~300ms tap-delay some mobile browsers apply while
       deciding whether a tap is a double-tap; also stops page scroll from
       hijacking scrub drags. */
    touch-action: none;
    outline: none;
  }

  /* Playhead: 1px line through the frame plus the ruler's grip wedge
     (13x12, polish 10-16 #2). Moved with translateX from the RAF tick. The
     line starts under the wedge's tip so the two read as one element. Only
     the grip's hit box takes pointer events: dragging it scrubs. */
  .playhead {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 0;
    pointer-events: none;
    opacity: 0;
    will-change: transform;
  }
  .playhead .line {
    position: absolute;
    top: 14px;
    bottom: 0;
    left: 0;
    width: 1px;
    background: var(--p-playhead);
  }
  /* The 1px line occupies x..x+1, so its centre is x+0.5; the hit box (and
     the grip inside it) is shifted by the same half pixel to stay centred
     on the line while the line itself stays on a whole pixel column. */
  .playhead .hit {
    position: absolute;
    top: 0;
    left: -9.5px;
    width: 20px;
    height: var(--p-ruler-h);   /* the whole ruler band at the playhead (polish 10-16 #2) */
    pointer-events: auto;
    /* App: PointingHandCursor over the wedge, DraggingHandCursor while
       dragging (time_ruler_component.hpp). The grabbing hand is applied to
       the whole card while a grip drag is in progress, so it holds wherever
       the captured pointer wanders. */
    cursor: pointer;
    touch-action: none;
  }
  :host([data-scrubbing]) .card,
  :host([data-scrubbing]) .card * {
    cursor: grabbing;
  }
  .playhead .grip {
    position: absolute;
    top: 3px;
    left: 3.5px;
    width: 13px;
    height: 12px;
    background: var(--p-playhead);
    clip-path: polygon(0 0, 100% 0, 100% 55%, 50% 100%, 0 55%);
    border-radius: 2px 2px 0 0;
  }

  .waveform-loading {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    pointer-events: none;
    color: var(--p-text);
    font-size: 13px;
    letter-spacing: 0.01em;
    /* Soft scrim so the indicator stays legible on top of a stale waveform
       (model switch keeps the previous peaks painted until new ones land). */
    background: rgba(6, 8, 10, 0.78);
  }
  .waveform-loading-label {
    line-height: 1;
  }

  /* ---- Right pane island (SettingsPanel::paint, design 5953:2765) -------- */

  .panel {
    position: relative;
    border-radius: var(--p-radius-island);
    border: 1px solid var(--p-pressed);
    background: linear-gradient(to bottom, var(--p-panel), rgba(12, 17, 20, 0));
    padding: 12px;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  /* FileInfoHeader: Roboto Mono 12 --72, 24 row + 8 gap + 1 divider */
  .file-row {
    height: 24px;
    display: flex;
    align-items: center;
    font-family: var(--p-mono);
    color: var(--p-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .divider {
    flex: 0 0 1px;
    height: 1px;
    background: var(--p-pressed);
    margin: 8px 0 12px;
  }

  /* LabeledDropdown: 10px label, 4 gap, 28px input row */
  .section-label {
    display: block;
    height: 16px;
    line-height: 16px;
    font-size: 10px;
    color: var(--p-text);
    margin-bottom: 4px;
  }
  .picker {
    position: relative;
  }
  .input-row {
    width: 100%;
    height: 28px;
    padding: 0 8px;
    border-radius: var(--p-radius-btn);
    border: 1px solid var(--p-active-pressed);
    background: var(--p-hover);
    color: var(--p-text-bright);
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    text-align: left;
  }
  .picker-btn:disabled {
    opacity: 0.5;
  }
  .picker-name {
    flex: 0 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .version {
    color: var(--p-muted-icon);
    font-size: 11px;
    flex: 0 0 auto;
  }
  .carrot {
    margin-left: auto;
    color: var(--p-text);
    flex: 0 0 auto;
  }
  .picker-btn.open .carrot {
    transform: scaleY(-1);
  }

  /* Popup (ThemeV2LookAndFeel::drawPopupMenu*): --4 fill, --40 border, r4,
     8px padding; 24px rows on a 28px pitch; headers 12 bold --96 with a --24
     underline; text at x16 (popup 8 + item 8). */
  .popup {
    position: absolute;
    left: 0;
    right: 0;
    top: calc(100% + 4px);
    z-index: 20;
    padding: 4px 8px 8px;
    border-radius: var(--p-radius-btn);
    border: 1px solid var(--p-active-pressed);
    background: var(--p-bg);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  }
  .popup-header {
    height: 24px;
    margin-top: 4px;
    line-height: 24px;
    font-weight: 700;
    color: var(--p-text-bright);
    border-bottom: 1px solid var(--p-pressed);
  }
  .popup-item {
    height: 24px;
    margin-top: 4px;
    padding: 0 8px;
    border-radius: var(--p-radius-btn);
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--p-text);
    cursor: pointer;
  }
  .popup-item.highlight {
    background: var(--p-hover);
  }
  .popup-item.selected {
    background: var(--p-pressed);
    color: var(--p-text-bright);
  }
  .popup-item .glyph {
    margin-left: auto;
    color: var(--p-text-bright);
    display: inline-flex;
  }

  /* ---- Stem rows (StemRowLayout.hpp kStandardRow) ------------------------ */

  .rows {
    margin-top: 24px;
    display: flex;
    flex-direction: column;
    /* Reserve room for the tallest stack (Drums: 5 rows × 44 − 12) so the
       widget's height does not jump between models. */
    min-height: 208px;
  }

  /* Speakers row: label | 28px count | − | + ; count right-aligned to the dB
     column (grid gaps 8 put its right edge at the dB box's right edge). */
  .speakers-row {
    height: 32px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 28px 20px 20px;
    column-gap: 8px;
    align-items: start;
  }
  .speakers-label {
    height: 20px;
    line-height: 20px;
    color: var(--p-text-bright);
  }
  .speakers-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .stem-row {
    display: flex;
    flex-direction: column;
    margin-bottom: 12px;
  }
  .stem-row:last-child {
    margin-bottom: 0;
  }
  /* Title line: 8px dot (gradient top -> base, thin ring), 4 gap, 12px label */
  .stem-title {
    height: 12px;
    display: flex;
    align-items: center;
    gap: 4px;
    color: var(--p-text-bright);
    white-space: nowrap;
  }
  .stem-dot {
    flex: 0 0 8px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: linear-gradient(to bottom, var(--stem-top), var(--stem));
    box-shadow: inset 0 0 0 0.67px var(--stem);
  }
  .stem-title label {
    line-height: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Controls line: slider 128 | gap 20 | dB 52 | 8 | S 20 | 8 | M 20 = 256.
     The slider column flexes on wider panes; the 20px gap is its margin. */
  .stem-controls {
    height: 20px;
    display: grid;
    grid-template-columns: minmax(96px, 1fr) 52px 20px 20px;
    column-gap: 8px;
    align-items: center;
  }
  /* Percent rows: 40px box on the dB column, trailing spacer keeps the
     slider at the same width as a dB row (40 + 8 + 60 = 52 + 8 + 20 + 8 + 20). */
  .stem-row.percent .stem-controls {
    grid-template-columns: minmax(96px, 1fr) 40px 60px;
  }
  .stem-row.dimmed .stem-title,
  .stem-row.dimmed .slider,
  .stem-row.dimmed .value-box {
    opacity: 0.4;
  }

  /* ---- Slider (StemSliderLookAndFeel): 4px track --32, stem fill, 16px thumb */
  .slider {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    height: 20px;
    margin: 0 12px 0 0;
    padding: 0;
    background: transparent;
    cursor: pointer;
    outline: none;
  }
  .slider:disabled {
    cursor: default;
    opacity: 0.5;
  }
  .slider::-webkit-slider-runnable-track {
    height: 4px;
    border-radius: 2px;
    background: linear-gradient(to right, var(--stem) 0 var(--fill), var(--p-track) var(--fill) 100%);
  }
  .slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    margin-top: -6px;
    border-radius: 50%;
    background: var(--thumb-fill);
    border: 1.6px solid var(--thumb-ring);
  }
  .slider::-moz-range-track {
    height: 4px;
    border-radius: 2px;
    background: var(--p-track);
  }
  .slider::-moz-range-progress {
    height: 4px;
    border-radius: 2px;
    background: var(--stem);
  }
  .slider::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--thumb-fill);
    border: 1.6px solid var(--thumb-ring);
  }

  /* ---- Value box (design 6005:16032): dark fill, --40 outline, r4, 12px ---- */
  .value-box {
    width: 100%;
    height: 20px;
    padding: 0;
    border-radius: var(--p-radius-btn);
    border: 1px solid var(--p-active-pressed);
    background: var(--p-bg);
    color: var(--p-text-bright);
    font: 12px var(--p-font);
    text-align: center;
    outline: none;
  }
  .value-box:focus {
    background: var(--p-hover);
  }
  .value-box:disabled {
    opacity: 0.5;
  }

  /* ---- S / M buttons (SMButtonLookAndFeel six-state machine) ------------- */
  .sm-btn {
    width: 20px;
    height: 20px;
    padding: 0;
    border-radius: var(--p-radius-btn);
    border: 1px solid var(--p-active-pressed);
    background: transparent;
    color: var(--p-text);
    font-size: 11px;
    line-height: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
  .sm-btn:hover:not(:disabled) {
    background: var(--p-hover);
    border-color: var(--p-active-hover);
  }
  .sm-btn:active:not(:disabled) {
    background: var(--p-pressed);
    border-color: var(--p-active);
    color: var(--p-text-bright);
  }
  .sm-btn[aria-pressed='true'] {
    background: var(--sm-fill);
    border-color: var(--stem);
    color: var(--p-text-bright);
  }
  .sm-btn[aria-pressed='true']:hover:not(:disabled) {
    background: var(--sm-fill-hover);
    border-color: var(--stem);
  }
  .sm-btn[aria-pressed='true']:active:not(:disabled) {
    background: var(--sm-fill-pressed);
  }
  .sm-btn:disabled {
    opacity: 0.5;
  }

  /* ---- Focus rings: keyboard users only (data-modality, see host) -------- */
  :host([data-modality='keyboard']) button:focus-visible,
  :host([data-modality='keyboard']) .value-box:focus-visible,
  :host([data-modality='keyboard']) canvas.waveform:focus-visible {
    outline: 2px solid var(--p-focus);
    outline-offset: 1px;
  }
  :host([data-modality='keyboard']) .slider:focus-visible::-webkit-slider-thumb {
    box-shadow: 0 0 0 2px var(--p-focus);
  }
  :host([data-modality='keyboard']) .slider:focus-visible::-moz-range-thumb {
    box-shadow: 0 0 0 2px var(--p-focus);
  }

  /* ---- Loading / error states ------------------------------------------- */
  .spinner {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(169, 177, 184, 0.3);
    border-top-color: var(--p-text-bright);
    border-radius: 50%;
    animation: pSpin 0.9s linear infinite;
  }
  @keyframes pSpin {
    to { transform: rotate(360deg); }
  }

  .error-panel {
    grid-column: 1 / -1;
    min-height: 240px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    border-radius: var(--p-radius-island);
    border: 1px solid var(--p-pressed);
    background: linear-gradient(to bottom, var(--p-panel), rgba(12, 17, 20, 0));
  }
  .error-panel p {
    margin: 0;
    font-size: 14px;
    color: var(--p-text-bright);
  }
  .error-panel .error-actions {
    display: flex;
    gap: 12px;
    align-items: center;
  }

  /* Screen-reader-only live region. */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .too-narrow {
    display: none;
    padding: 24px;
    border-radius: var(--p-radius-island);
    background: var(--p-bg);
    color: var(--p-text);
    text-align: center;
    font-size: 13px;
  }

  @media (prefers-reduced-motion: reduce) {
    .play-btn.pulse {
      animation: none;
    }
    .play-btn.pulse::before {
      animation: none;
      box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.7);
    }
    .spinner {
      animation: none;
    }
  }

  /* ---- Responsive (container queries: the widget's own width) ----------- */

  /* Below the minimum usable width, show a placeholder instead of the widget.
     The floor is the timestamp island (218px, nowrap mono text) plus the
     card's 24px padding: 244px keeps a 320px phone with a padded page
     (iPhone SE: 272px for the widget) on the real layout; the stem row still
     fits (slider ≥ 96, value 52, S/M 20+20, gaps) and the meter shrinks. */
  @container photon (max-width: 243px) {
    .card {
      display: none;
    }
    .too-narrow {
      display: block;
    }
  }

  /* Narrow: nav wraps onto two rows, waveform on top, pane beneath. */
  @container photon (max-width: 639px) {
    /* Brand row = brand + the branding icon inline at its right (the icon
       leaves the absolute slot so narrow rows get the full width); the
       islands stack beneath. */
    .nav {
      padding-right: 0;
    }
    .brand {
      flex: 1 0 calc(100% - 40px);
      margin-right: 0;
      order: -2;
    }
    .branding {
      position: static;
      order: -1;
      margin-left: auto;
    }
    .island.meter {
      order: 10;
    }
    .body {
      grid-template-columns: minmax(0, 1fr);
    }
    .wave-frame {
      min-height: 160px;
    }
    .rows {
      min-height: 0;
    }
  }
`;
